import { useEditor } from "features/editor/Feature";
import type { SandboxEditor } from "features/types/editor";
import {
  Text,
  InputWithSuggestions,
  Tooltip,
  Button,
  Badge,
  Stack,
  Icon,
  styled,
} from "prism-react";
import { useRef, useState } from "react";

import { useKeyboardShortcuts } from "hooks/useKeyboardShortcuts";

import type { Props } from "./types";

export const SearchDependency: React.FC<
  Pick<Props, "onDependencySelect" | "onSearchDependency" | "disabled">
> = ({ onDependencySelect, onSearchDependency, disabled }) => {
  const editor = useEditor();
  const isDevbox = editor.type === "sandbox" && editor.isCloud;
  const [devMode, setDevMode] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const debounceTimer = useRef<NodeJS.Timeout>();

  const [searchResults, setDependencySearchResult] = useState<
    Array<{ name: string; version: string }>
  >([]);

  const onInputChange = (query: string) => {
    setInputValue(query);
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (query === "") {
      setDependencySearchResult([]);

      return;
    }

    debounceTimer.current = setTimeout(async () => {
      const data = await onSearchDependency(query);

      setDependencySearchResult(
        data.slice(0, 5).map(({ name, version }) => ({ name, version })),
      );
    }, 300);
  };

  const handleDependencySelect = (
    name: string,
    version: string,
    dev: boolean,
  ) => {
    onDependencySelect(name, version, dev);
    setDependencySearchResult([]);
    setInputValue("");
  };

  let privateScope = false;
  if (editor.type === "sandbox") {
    privateScope = isPrivateScope(editor, inputValue);
  }

  useKeyboardShortcuts(
    privateScope
      ? {
          Enter: () => {
            handleDependencySelect(inputValue, "latest", devMode);
          },
        }
      : {},
    [privateScope],
  );

  const devDepButton = isDevbox ? (
    <Tooltip content="Add as dev dependency">
      <Button
        css={{
          position: "absolute",
          right: 4,
          top: 2,
          color: devMode ? "$neutral-fg-high" : "$neutral-fg-base",
        }}
        onClick={() => {
          setDevMode((prev) => !prev);
        }}
      >
        <Badge
          css={{
            height: "$4",
            px: 6,
            color: devMode ? "$neutral-bg-low" : "$neutral-fg-base",
            background: devMode ? "$accent-secondary-base" : "transparent",
          }}
          textCss={{ fontSize: "10px" }}
        >
          DEV
        </Badge>
      </Button>
    </Tooltip>
  ) : null;

  return (
    <>
      <InputWithSuggestions<{ name: string; version: string }>
        appendElement={devDepButton}
        disabled={disabled}
        icon="search"
        inputCss={{
          width: "calc(100% - 4px)",
          marginLeft: 2,
          marginBottom: "$3",
          height: "$8",
        }}
        items={privateScope ? [] : searchResults}
        onInputValueChange={onInputChange}
        onSuggestionSelected={({ name, version }) =>
          handleDependencySelect(name, version, devMode)
        }
        placeholder="Search..."
        value={inputValue}
      >
        {({ name, version }) => {
          return (
            <>
              <Text color="neutral-fg-subtle" truncate>
                {name}
              </Text>
              <Text as="pre" truncate>
                {version}
              </Text>
            </>
          );
        }}
      </InputWithSuggestions>

      {privateScope && (
        <StyledAutoCompleteContainer>
          <Stack css={{ color: "$neutral-fg-high" }} horizontal>
            <Icon size={4} type="help" />
            <Text>Private scope</Text>
          </Stack>

          <Text color="neutral-fg-high">
            Add this package by typing its full name and pressing “Enter”.
          </Text>
        </StyledAutoCompleteContainer>
      )}
    </>
  );
};

const StyledAutoCompleteContainer = styled(Stack, {
  backgroundColor: "$neutral-bg-medium",
  padding: "$3",
  borderRadius: "$2",
  boxShadow: "$popup",
  position: "absolute",
  zIndex: "$menu",
  left: "$4",
  right: "$4",
});

const isPrivateScope = (sandbox: SandboxEditor, input: string): boolean => {
  if (!input.startsWith("@")) {
    return false;
  }

  const [scope] = input.split("/");

  const registries = sandbox.npmRegistries;
  return registries.some(
    (r) => r.limitToScopes && r.enabledScopes.some((s) => s === scope),
  );
};
